import React, { useState, useEffect } from 'react';
import { X, User, CreditCard, Activity, Home, Trophy, Shield, ChevronRight, CheckCircle, Clock, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ADMIN_KEY = 'rentalx-admin-2026';
const BRAND = 'linear-gradient(135deg,#2cbdc4,#094f6f)';

const fmt = n => Number(n || 0).toLocaleString();
const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDateShort = iso => iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const VIBE_LABELS = {
  social_anchor: 'Social Anchor', quiet_professional: 'Quiet Professional',
  night_owl: 'Night Owl', early_riser: 'Early Riser',
  free_spirit: 'Free Spirit', homebody: 'Homebody',
};

const TABS = [
  { id: 'profile',    label: 'Profile',       icon: User },
  { id: 'payments',   label: 'Payments',      icon: CreditCard },
  { id: 'activity',   label: 'Activity',      icon: Activity },
  { id: 'listings',   label: 'Listings',      icon: Home },
  { id: 'rewards',    label: 'Rewards',       icon: Trophy },
];

export default function UserDetailModal({ userId, onClose, onActionComplete }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [actionMsg, setActionMsg] = useState('');
  const [adjustPts, setAdjustPts] = useState('');
  const [adjustReason, setAdjustReason] = useState('');

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    fetch(`${API}/api/admin/users/${userId}/detail?key=${ADMIN_KEY}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [userId]);

  const action = async (endpoint, method = 'PATCH', body = null) => {
    const opts = { method, headers: { 'Content-Type': 'application/json' } };
    if (body) opts.body = JSON.stringify(body);
    const r = await fetch(`${API}${endpoint}?key=${ADMIN_KEY}`, opts);
    const d = await r.json();
    return d;
  };

  const handleToggleActive = async () => {
    const u = data?.user;
    if (!u) return;
    const ep = u.is_active ? `/api/admin/users/${userId}/deactivate` : `/api/admin/users/${userId}/activate`;
    const d = await action(ep);
    setActionMsg(d.status === 'deactivated' ? 'User deactivated' : 'User activated');
    setData(prev => ({ ...prev, user: { ...prev.user, is_active: !u.is_active } }));
    onActionComplete?.();
  };

  const handleForceLogout = async () => {
    await action(`/api/admin/users/${userId}/force-logout`, 'POST');
    setActionMsg('All sessions invalidated');
  };

  const handleAdjustPts = async () => {
    if (!adjustPts || !adjustReason) return;
    const d = await action('/api/rewards/admin/adjust', 'POST', {
      email: data?.user?.email, points: parseInt(adjustPts), reason: adjustReason
    });
    setActionMsg(`Points adjusted — new balance: ${fmt(d.new_balance)}`);
    setAdjustPts(''); setAdjustReason('');
    // Refresh
    const fresh = await fetch(`${API}/api/admin/users/${userId}/detail?key=${ADMIN_KEY}`).then(r => r.json());
    setData(fresh);
  };

  if (!userId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(4,30,43,0.7)', backdropFilter: 'blur(8px)' }}>
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-7 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-black text-lg flex-shrink-0"
              style={{ background: BRAND }}>
              {data?.user?.full_name?.[0]?.toUpperCase() || '?'}
            </div>
            <div>
              <h2 className="text-xl font-black text-gray-900">{data?.user?.full_name || 'Loading…'}</h2>
              <p className="text-sm text-gray-400">{data?.user?.email}</p>
            </div>
            {data?.user && (
              <div className="flex gap-2 ml-2">
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${data.user.role === 'landlord' ? 'bg-purple-100 text-purple-700' : 'bg-sky-100 text-sky-700'}`}>
                  {data.user.role}
                </span>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${data.user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                  {data.user.is_active ? 'Active' : 'Inactive'}
                </span>
                {data.user.verification_status === 'verified' && (
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-teal-100 text-teal-700 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Verified
                  </span>
                )}
              </div>
            )}
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Quick admin actions */}
        {data && (
          <div className="flex gap-2 px-7 py-3 bg-gray-50 border-b border-gray-100 flex-shrink-0 flex-wrap">
            <button onClick={handleToggleActive}
              className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${data.user.is_active ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-green-200 text-green-600 hover:bg-green-50'}`}>
              {data.user.is_active ? 'Deactivate User' : 'Activate User'}
            </button>
            <button onClick={handleForceLogout} className="text-xs px-3 py-1.5 rounded-lg border border-orange-200 text-orange-600 hover:bg-orange-50 font-medium transition-colors">
              Force Logout
            </button>
            {actionMsg && <span className="text-xs text-green-600 bg-green-50 border border-green-200 px-3 py-1.5 rounded-lg font-medium">{actionMsg}</span>}
          </div>
        )}

        {/* Tab nav */}
        <div className="flex gap-0 px-7 pt-2 border-b border-gray-100 flex-shrink-0 overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all whitespace-nowrap ${
                  activeTab === t.id ? 'border-[#094f6f] text-[#094f6f]' : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}>
                <Icon className="w-3.5 h-3.5" />{t.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-7">
          {loading && <div className="py-16 text-center text-gray-400 text-sm">Loading user data…</div>}

          {!loading && data && (
            <>
              {/* ── PROFILE ── */}
              {activeTab === 'profile' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <InfoBox label="Full Name" value={data.user.full_name} />
                    <InfoBox label="Email" value={data.user.email} />
                    <InfoBox label="Role" value={data.user.role} />
                    <InfoBox label="Auth Provider" value={data.user.auth_provider || 'email'} />
                    <InfoBox label="Joined" value={fmtDateShort(data.user.created_at)} />
                    <InfoBox label="Onboarding" value={data.user.onboarding_completed ? '✓ Completed' : '✗ Pending'} />
                    <InfoBox label="Verification" value={data.user.verification_status || 'unverified'} />
                    <InfoBox label="Grace Days Left" value={data.user.grace_period_start ? '—' : 'N/A'} />
                    <InfoBox label="VibeMate" value={data.vibe ? (VIBE_LABELS[data.vibe.vibe_type] || data.vibe.vibe_type) : 'Not set'} />
                    <InfoBox label="Referrals Made" value={`${data.referrals_made} confirmed`} />
                  </div>

                  {/* Verification detail */}
                  {data.verification && (
                    <div className="border border-gray-200 rounded-2xl p-5">
                      <p className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2"><Shield className="w-4 h-4 text-[#094f6f]" /> ID Verification</p>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <InfoBox label="Document Type" value={data.verification.document_type} />
                        <InfoBox label="Status" value={data.verification.status} />
                        {data.verification.admin_note && <InfoBox label="Admin Note" value={data.verification.admin_note} />}
                        <InfoBox label="Submitted" value={fmtDateShort(data.verification.created_at)} />
                      </div>
                    </div>
                  )}

                  {/* Grace Period Extension */}
                  <GraceExtensionPanel userId={userId} user={data.user} onDone={(msg) => { setActionMsg(msg); }} />
                </div>
              )}

              {/* ── PAYMENTS ── */}
              {activeTab === 'payments' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Payment History ({data.payments.length})</h3>
                  {data.payments.length === 0
                    ? <EmptyState text="No payments recorded" />
                    : (
                      <div className="rounded-2xl border border-gray-200 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead><tr className="bg-gray-50 border-b"><th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase">Package</th><th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase">Amount</th><th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase">Status</th><th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase">Date</th></tr></thead>
                          <tbody>
                            {data.payments.map((p, i) => (
                              <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                                <td className="px-4 py-3 font-medium text-gray-800">{p.package_id || '—'}</td>
                                <td className="px-4 py-3 text-[#094f6f] font-bold">{p.amount ? `A$${p.amount}` : '—'}</td>
                                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{p.payment_status || 'unknown'}</span></td>
                                <td className="px-4 py-3 text-gray-400 text-xs">{fmtDateShort(p.created_at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  }
                </div>
              )}

              {/* ── ACTIVITY ── */}
              {activeTab === 'activity' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Activity Log ({data.activity.length})</h3>
                  {data.activity.length === 0
                    ? <EmptyState text="No activity recorded yet" />
                    : (
                      <div className="space-y-2">
                        {data.activity.map((a, i) => (
                          <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                            <div className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                              <Activity className="w-3.5 h-3.5 text-gray-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800">{a.event}</p>
                              {a.details && Object.keys(a.details).length > 0 && (
                                <p className="text-xs text-gray-400 truncate">{JSON.stringify(a.details)}</p>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 flex-shrink-0">{fmtDate(a.created_at)}</p>
                          </div>
                        ))}
                      </div>
                    )
                  }
                </div>
              )}

              {/* ── LISTINGS / REQUIREMENTS ── */}
              {activeTab === 'listings' && (
                <div className="space-y-5">
                  {data.user.role === 'landlord' ? (
                    <>
                      <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Property Listings ({data.listings.length})</h3>
                      {data.listings.length === 0
                        ? <EmptyState text="No listings" />
                        : (
                          <div className="rounded-2xl border border-gray-200 overflow-hidden">
                            <table className="w-full text-sm">
                              <thead><tr className="bg-gray-50 border-b"><th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase">Name</th><th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase">City</th><th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase">Price</th><th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase">Status</th><th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase">Visibility</th></tr></thead>
                              <tbody>
                                {data.listings.map((l, i) => (
                                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                                    <td className="px-4 py-3 font-medium text-gray-800 max-w-[180px] truncate">{l.name}</td>
                                    <td className="px-4 py-3 text-gray-500">{l.city}</td>
                                    <td className="px-4 py-3 text-[#094f6f] font-bold">A${l.price_per_week}/wk</td>
                                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${l.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{l.status}</span></td>
                                    <td className="px-4 py-3"><span className="text-xs text-gray-400">{l.visibility || 'public'}</span></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )
                      }
                    </>
                  ) : (
                    <>
                      <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Accommodation Requirements ({data.requirements.length})</h3>
                      {data.requirements.length === 0
                        ? <EmptyState text="No requirements posted" />
                        : (
                          <div className="space-y-2">
                            {data.requirements.map((r, i) => (
                              <div key={i} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                                <div>
                                  <p className="text-sm font-medium text-gray-800">{r.title}</p>
                                  <p className="text-xs text-gray-400">{r.location} · A${r.budget}/wk</p>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${r.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{r.status}</span>
                              </div>
                            ))}
                          </div>
                        )
                      }

                      {/* Rent receipts */}
                      {data.receipts.length > 0 && (
                        <>
                          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mt-6">Rent Receipts ({data.receipts.length})</h3>
                          <div className="space-y-2">
                            {data.receipts.map((r, i) => (
                              <div key={i} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                                <div>
                                  <p className="text-sm font-medium text-gray-800">{r.property_address}</p>
                                  <p className="text-xs text-gray-400">A${r.rent_amount} · {r.payment_frequency}</p>
                                </div>
                                <div className="text-right">
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${r.status === 'approved' ? 'bg-green-100 text-green-700' : r.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>{r.status}</span>
                                  {r.points_awarded > 0 && <p className="text-xs text-[#094f6f] font-bold mt-0.5">+{r.points_awarded} pts</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ── REWARDS ── */}
              {activeTab === 'rewards' && (
                <div className="space-y-5">
                  {/* Balance summary */}
                  <div className="rounded-2xl p-5 text-white" style={{ background: 'linear-gradient(160deg,#041e2b,#094f6f)' }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white/50 text-xs uppercase tracking-wide mb-1">Points Balance</p>
                        <p className="text-4xl font-black">{fmt(data.rewards?.balance)}</p>
                        <p className="text-[#2cbdc4] text-sm font-medium mt-1">A${((data.rewards?.balance || 0) / 1000).toFixed(2)} value · {fmt(data.rewards?.lifetime_earned)} lifetime</p>
                      </div>
                      <Trophy className="w-10 h-10 text-[#2cbdc4]" />
                    </div>
                  </div>

                  {/* Manual adjust */}
                  <div className="border border-gray-200 rounded-2xl p-5">
                    <p className="text-sm font-bold text-gray-700 mb-3">Manual Points Adjustment</p>
                    <div className="flex gap-2 flex-wrap">
                      <input value={adjustPts} onChange={e => setAdjustPts(e.target.value)} type="number"
                        placeholder="+100 or -100" className="flex-1 min-w-[120px] border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none" />
                      <input value={adjustReason} onChange={e => setAdjustReason(e.target.value)}
                        placeholder="Reason" className="flex-[2] min-w-[200px] border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none" />
                      <button onClick={handleAdjustPts}
                        className="px-4 py-2 rounded-xl text-white text-sm font-bold hover:opacity-90 transition-opacity"
                        style={{ background: BRAND }}>
                        Apply
                      </button>
                    </div>
                  </div>

                  {/* Recent transactions */}
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Recent Transactions</h3>
                  {data.transactions.length === 0
                    ? <EmptyState text="No transactions yet" />
                    : (
                      <div className="rounded-2xl border border-gray-200 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead><tr className="bg-gray-50 border-b"><th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase">Event</th><th className="text-right px-4 py-2.5 text-xs font-bold text-gray-500 uppercase">Points</th><th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase">Date</th></tr></thead>
                          <tbody>
                            {data.transactions.map((t, i) => (
                              <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                                <td className="px-4 py-3 text-gray-700">{t.description || t.event}</td>
                                <td className={`px-4 py-3 text-right font-black ${t.points > 0 ? 'text-emerald-600' : 'text-red-500'}`}>{t.points > 0 ? '+' : ''}{fmt(t.points)}</td>
                                <td className="px-4 py-3 text-gray-400 text-xs">{fmtDateShort(t.created_at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  }
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoBox({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-xl px-4 py-3">
      <p className="text-xs text-gray-400 mb-0.5 font-medium">{label}</p>
      <p className="text-sm font-semibold text-gray-800 truncate">{value || '—'}</p>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="py-10 text-center text-gray-400 text-sm bg-gray-50 rounded-2xl">
      {text}
    </div>
  );
}

function GraceExtensionPanel({ userId, user, onDone }) {
  const [days, setDays] = useState(14);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const isVerified = user?.verification_status === 'verified';

  const extend = async () => {
    setSaving(true);
    try {
      const r = await fetch(
        `${API}/api/admin/users/${userId}/extend-grace?key=${ADMIN_KEY}&extra_days=${days}&note=${encodeURIComponent(note)}`,
        { method: 'PATCH' }
      );
      const d = await r.json();
      if (r.ok) {
        onDone(`Grace period reset — user now has ${days} days from today`);
        setNote('');
      } else {
        onDone(`Error: ${d.detail}`);
      }
    } catch { onDone('Network error'); }
    setSaving(false);
  };

  if (isVerified) return null;

  return (
    <div className="border border-amber-200 bg-amber-50 rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-4 h-4 text-amber-600" />
        <p className="text-sm font-bold text-amber-800">Extend Verification Grace Period</p>
      </div>
      <p className="text-xs text-amber-700 mb-4 leading-relaxed">
        Reset this user&apos;s grace period from today, giving them a fresh verification window.
        Current status: <strong>{user?.verification_status || 'unverified'}</strong>
      </p>
      <div className="flex flex-wrap gap-2 mb-3">
        {[7, 14, 30].map(d => (
          <button key={d} onClick={() => setDays(d)}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all ${
              days === d ? 'border-amber-500 bg-amber-500 text-white' : 'border-amber-200 text-amber-700 hover:border-amber-400'
            }`}>
            +{d} days
          </button>
        ))}
      </div>
      <input value={note} onChange={e => setNote(e.target.value)}
        placeholder="Optional admin note (e.g. 'Awaiting passport renewal')"
        className="w-full border border-amber-200 rounded-xl px-3 py-2 text-xs mb-3 focus:outline-none bg-white" />
      <button onClick={extend} disabled={saving}
        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-xs font-bold disabled:opacity-60 hover:opacity-90 transition-opacity"
        style={{ background: 'linear-gradient(135deg,#F59E0B,#D97706)' }}>
        {saving ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Clock className="w-3.5 h-3.5" />}
        {saving ? 'Extending…' : `Extend by ${days} days from today`}
      </button>
    </div>
  );
}
